import { Cart, CartItem, Product, ProductVariant } from '@/lib/shopify/types';

export interface EnhancedPricingData {
  subtotal: string;
  totalDiscount: string;
  finalTotal: string;
  currencyCode: string;
  hasDiscounts: boolean;
  itemDiscounts: Array<{
    merchandiseId: string;
    originalPrice: number;
    currentPrice: number;
    savings: number;
    hasDiscount: boolean;
  }>;
}

export interface CartItemPricing {
  currentPrice: number;
  originalPrice: number;
  savings: number;
  hasDiscount: boolean;
  currencyCode: string;
}

/**
 * Enhanced pricing calculator for cart items and totals
 * Handles multiple discount sources: compareAtPrice, quantity discounts, promo codes
 */
export class EnhancedPricingCalculator {
  
    /**
   * Calculate pricing for a single cart item
   */
  static calculateItemPricing(item: CartItem, productData?: Product): CartItemPricing {
    const currentTotalPrice = parseFloat(item.cost.totalAmount.amount);
    const currencyCode = item.cost.totalAmount.currencyCode;
    const quantity = item.quantity;
    
    // Calculate per-unit current price
    const currentUnitPrice = currentTotalPrice / quantity;
    
    let originalTotalPrice = currentTotalPrice;
    let hasDiscount = false;

    // Try to get compareAtPrice from product data
    if (productData) {
      let variants: ProductVariant[] = [];
      
      if (Array.isArray(productData.variants)) {
        variants = productData.variants;
      } else if (productData.variants && typeof productData.variants === 'object') {
        const variantsData = productData.variants as any;
        if (variantsData.edges && Array.isArray(variantsData.edges)) {
          variants = variantsData.edges.map((edge: any) => edge.node);
        }
      }

      // Find the current variant
      const currentSelectedOptions: Record<string, string> = {};
      item.merchandise.selectedOptions.forEach(({ name, value }) => {
        currentSelectedOptions[name.toLowerCase()] = value;
      });

      const currentVariant = variants.find((variant: ProductVariant) =>
        variant.selectedOptions.every((option) => {
          const optionNameLower = option.name.toLowerCase();
          return currentSelectedOptions[optionNameLower] === option.value;
        })
      );

      // Get compareAtPrice from variant or product (this is per-unit price)
      const compareAtUnitPrice = currentVariant?.compareAtPrice?.amount || 
                                productData.compareAtPriceRange?.maxVariantPrice?.amount;

      if (compareAtUnitPrice) {
        const compareAtUnitValue = parseFloat(compareAtUnitPrice);
        // Compare per-unit prices to determine if there's a real discount
        if (compareAtUnitValue > currentUnitPrice) {
          // Calculate total original price (per-unit compareAt * quantity)
          originalTotalPrice = compareAtUnitValue * quantity;
          hasDiscount = true;
        }
      }
    }

    return {
      currentPrice: currentTotalPrice,
      originalPrice: originalTotalPrice,
      savings: hasDiscount ? originalTotalPrice - currentTotalPrice : 0,
      hasDiscount,
      currencyCode
    };
  }

  /**
   * Calculate enhanced pricing data for entire cart
   */
  static calculateCartPricing(cart: Cart | null | undefined, productDataMap?: Map<string, Product>): EnhancedPricingData {
    if (!cart || !cart.lines || cart.lines.length === 0) {
      return {
        subtotal: '0.00',
        totalDiscount: '0.00',
        finalTotal: '0.00',
        currencyCode: 'USD',
        hasDiscounts: false,
        itemDiscounts: []
      };
    }

    const currencyCode = cart.cost?.totalAmount?.currencyCode || 'USD';
    let totalOriginalPrice = 0;
    let totalCurrentPrice = 0;
    const itemDiscounts: EnhancedPricingData['itemDiscounts'] = [];

    // Calculate item-level pricing
    cart.lines.forEach((item) => {
      const productData = productDataMap?.get(item.merchandise.product.handle);
      const itemPricing = this.calculateItemPricing(item, productData);
      
      totalCurrentPrice += itemPricing.currentPrice;
      totalOriginalPrice += itemPricing.originalPrice;
      
      itemDiscounts.push({
        merchandiseId: item.merchandise.id,
        originalPrice: itemPricing.originalPrice,
        currentPrice: itemPricing.currentPrice,
        savings: itemPricing.savings,
        hasDiscount: itemPricing.hasDiscount
      });
    });

    // Use cart total if available (includes Shopify-level discounts)
    const cartTotal = parseFloat(cart.cost?.totalAmount?.amount || '0');
    const subtotalAmount = parseFloat(cart.cost?.subtotalAmount?.amount || cartTotal.toString());
    
    // Calculate total discount
    const totalDiscount = Math.max(0, totalOriginalPrice - cartTotal);
    const hasDiscounts = totalDiscount > 0;

    return {
      subtotal: subtotalAmount.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      finalTotal: cartTotal.toFixed(2),
      currencyCode,
      hasDiscounts,
      itemDiscounts
    };
  }

  /**
   * Format currency value
   */
  static formatCurrency(amount: string | number, currencyCode: string): string {
    const value = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Get discount percentage for display
   */
  static getDiscountPercentage(originalPrice: number, currentPrice: number): number {
    if (originalPrice <= currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  }
} 